import { useCallback, useMemo, useRef, useState } from 'react';
import { LayoutChangeEvent, View } from 'react-native';
import { isWeb } from '@helpers';
import type { ImageDocumentStoreItem } from '@v2/components/imageHandling/types/types';
import { ANNOTATION_LIST_DEFAULT_WIDTH } from '@v2/components/imageHandling/components/DrawingCanvas/constants';
import { getAngle, shouldSwapDimensions } from '@v2/components/imageHandling/helpers';
import { buildCropBounds, getCanvasCursor, normalizeRotation } from '@v2/components/imageHandling/components/DrawingCanvas/helpers';
import { AnnotationType, ImageTransformInputs, TextAnnotation, TextEditingRequest, Viewport } from '@v2/components/imageHandling/components/DrawingCanvas/types';
import { useKeyboardShortcuts, useManageDrawingCanvas, usePointerInteractions, useTextEditing } from '.';
import { useImageViewTransformController } from './useImageViewTransformController';
import { useManageDrawingCanvasLayout } from './useManageDrawingCanvasLayout';

export interface UseDrawingSessionParams {
  image: ImageDocumentStoreItem;
  pageNumber: number;
  currentUserId: number;
  onExit: () => void;
  annotationListWidth?: number;
}

/**
 * Single hook that bundles every piece of drawing-mode state — annotations API, image
 * zoom/pan controller, text editing, pointer interactions, layout chrome — so the state
 * can be lifted to ImageHandling and shared between Picture (annotation overlay slot
 * inside Zoomable on native) and DrawingCanvas (chrome).
 *
 * Returning one object means a single source of truth and stable references for everything
 * downstream — re-renders happen only when meaningful state changes.
 */
export const useDrawingSession = ({
  image,
  pageNumber,
  currentUserId,
  onExit,
  annotationListWidth = ANNOTATION_LIST_DEFAULT_WIDTH,
}: UseDrawingSessionParams) => {
  const [layout, setLayout] = useState<Viewport>({ viewportWidth: 0, viewportHeight: 0 });
  const containerRef = useRef<View | null>(null);
  const webContainerRef = useRef<HTMLDivElement | null>(null);

  const layoutChrome = useManageDrawingCanvasLayout({ annotationListWidth });

  const handleLayout = useCallback((e: LayoutChangeEvent) => {
    const { width, height } = e.nativeEvent.layout;
    setLayout({ viewportWidth: width, viewportHeight: height });
  }, []);

  const crop = useMemo(() => buildCropBounds(image, pageNumber), [image, pageNumber]);
  const documentRotation = useMemo(() => normalizeRotation(getAngle(image)), [image]);

  const api = useManageDrawingCanvas({
    image,
    pageNumber,
    viewport: layout,
    documentRotation,
    currentUserId,
    onExit,
  });

  const transformController = useImageViewTransformController({
    withScalingPanning: true,
    rotationAngle: documentRotation,
    documentId: image.documentId,
  });
  const viewTransform = useMemo(
    () => ({ zoom: transformController.zoom, panX: transformController.panX, panY: transformController.panY }),
    [transformController.zoom, transformController.panX, transformController.panY]
  );

  const workingWidth = shouldSwapDimensions(documentRotation) ? image.heightOriginal : image.widthOriginal;
  const workingHeight = shouldSwapDimensions(documentRotation) ? image.widthOriginal : image.heightOriginal;

  const transformInputs: ImageTransformInputs = useMemo(
    () => ({
      widthOriginal: image.widthOriginal,
      heightOriginal: image.heightOriginal,
      documentRotation,
      crop,
      viewportWidth: layout.viewportWidth || workingWidth,
      viewportHeight: layout.viewportHeight || workingHeight,
    }),
    [image.widthOriginal, image.heightOriginal, documentRotation, crop, layout, workingWidth, workingHeight]
  );

  const textEditing = useTextEditing({
    annotations: api.annotations,
    setAnnotations: api.setAnnotations,
    pushUndoSnapshot: api.pushUndoSnapshot,
  });

  const hitThresholdBase = Math.min(workingWidth, workingHeight) || 1000;
  const hitThreshold = hitThresholdBase * 0.022;
  const pointer = usePointerInteractions({
    api,
    hitThresholdBase,
    onRequestTextEditing: (req: TextEditingRequest) => textEditing.requestEditing(req),
    onBackgroundPanStart: transformController.handleMouseDown,
    onBackgroundPanMove: (e) => transformController.handleMouseMove(e, webContainerRef.current ?? undefined),
    onBackgroundPanEnd: transformController.handleMouseUp,
    onBackgroundDoubleClick: (e) => transformController.handleDoubleClick(e, webContainerRef.current ?? undefined),
  });

  // Tap-outside-to-dismiss while a text edit is in progress: commit pending text and swallow.
  const handlePointerDown = useCallback(
    (point: Parameters<typeof pointer.handlePointerDown>[0], e: Parameters<typeof pointer.handlePointerDown>[1]) => {
      if (textEditing.editing) {
        textEditing.commit();
        return;
      }
      pointer.handlePointerDown(point, e);
    },
    [pointer, textEditing]
  );

  const handleDoubleClick = useCallback(
    (point: Parameters<typeof pointer.handleDoubleClick>[0], e: Parameters<typeof pointer.handleDoubleClick>[1]) => {
      if (textEditing.editing) {
        textEditing.commit();
        return;
      }
      pointer.handleDoubleClick(point, e);
    },
    [pointer, textEditing]
  );

  useKeyboardShortcuts({
    isEditingText: !!textEditing.editing,
    hasSelection: !!api.selectedId,
    onDeleteSelected: api.deleteSelected,
    onUndo: api.undo,
    onRedo: api.redo,
    onEscape: () => {
      if (textEditing.editing) textEditing.cancel();
      else api.setSelectedId(null);
    },
  });

  const selected = api.selectedId ? api.annotations.find((a) => a.id === api.selectedId) ?? null : null;
  const showFillToggle =
    api.activeTool === AnnotationType.Rectangle ||
    api.activeTool === AnnotationType.Circle ||
    (selected != null && (selected.type === AnnotationType.Rectangle || selected.type === AnnotationType.Circle));

  const cursor = useMemo(
    () =>
      getCanvasCursor({
        dragMode: api.dragMode,
        activeTool: api.activeTool,
        hoveredHandle: pointer.hoveredHandle ?? null,
        hoveredAnnotationId: pointer.hoveredAnnotationId,
        hasSelection: !!selected,
        isOverSelectionBody: selected ? pointer.hoveredAnnotationId === selected.id : false,
        isZoomed: transformController.zoom > 1,
        isPanning: transformController.isDragging,
      }),
    [
      api.dragMode,
      api.activeTool,
      pointer.hoveredHandle,
      pointer.hoveredAnnotationId,
      selected,
      transformController.zoom,
      transformController.isDragging,
    ]
  );

  const editingAnnotation = textEditing.editing
    ? ((api.annotations.find((a) => a.id === textEditing.editing?.annotationId) ?? null) as TextAnnotation | null)
    : null;

  const containerRect = isWeb ? webContainerRef.current?.getBoundingClientRect() ?? null : null;
  const svgEl = isWeb ? (webContainerRef.current?.querySelector('svg') as SVGSVGElement | null) : null;
  const svgCtm = svgEl?.getScreenCTM() ?? null;

  return {
    // Refs
    containerRef,
    webContainerRef,
    // Layout
    layout,
    setLayout,
    handleLayout,
    layoutChrome,
    // Annotation API
    api,
    // Image transform
    transformController,
    viewTransform,
    // Text editing
    textEditing,
    editingAnnotation,
    // Pointer
    pointer,
    handlePointerDown,
    handleDoubleClick,
    // Derived
    selected,
    showFillToggle,
    cursor,
    transformInputs,
    crop,
    documentRotation,
    hitThreshold,
    workingWidth,
    workingHeight,
    // Web-only
    containerRect,
    svgCtm,
    annotationListWidth,
  };
};

export type DrawingSession = ReturnType<typeof useDrawingSession>;
